/**
 * What the client MEANT, as opposed to the OpenSearch DSL it built to express it.
 *
 * The cloud search Lambda reads only `event.body` on POST, so these parameters are
 * invisible to it and never reach OpenSearch. A local kernel, which cannot execute
 * OpenSearch DSL, reads these instead of reverse-engineering the query tree.
 */
export interface SearchIntent {
  query?: string
  size?: number
  /** Rows already seen, for infinite scroll. The DSL carries this too, in the body. */
  from?: number
  parentId?: string
  types?: string[]
  tags?: string[]
}

/** @returns a query-string fragment beginning with `&`, or `''` when there is nothing to say */
export const toIntentParams = (intent?: SearchIntent) => {
  if (!intent) {
    return ''
  }
  const params = new URLSearchParams()
  if (intent.query) {
    params.set('q', intent.query)
  }
  if (intent.size !== undefined) {
    params.set('size', String(intent.size))
  }
  /** Page one is the default on both hosts, so there is nothing to say about it */
  if (intent.from) {
    params.set('from', String(intent.from))
  }
  if (intent.parentId) {
    params.set('parentId', intent.parentId)
  }
  /**
   * Comma-joined rather than repeated keys: the local host reads its parameters through
   * `Object.fromEntries(url.searchParams)`, which keeps only the last value of a repeated
   * key and would drop every filter but one, silently.
   */
  if (intent.types && intent.types.length > 0) {
    params.set('type', intent.types.join(','))
  }
  if (intent.tags && intent.tags.length > 0) {
    params.set('tag', intent.tags.join(','))
  }
  const encoded = params.toString()
  return encoded ? `&${encoded}` : ''
}
